import cx from 'classnames';
import { Icon } from '@fluentui/react';
import { motion } from 'framer-motion';
import { FC, HTMLProps, useEffect, useRef, useState } from 'react';
import { VariableSizeList } from 'react-window';

import useHandleOutsideClicks from '../helpers/useHandleOutsideClicks';

import Button from './Button';
import Text from './Text';
import Tooltip from './Tooltip';
import Item from './Picklist/Item';

export type StandardPicklistItem = {
  type?: never;
  key: string | number;
  value: string | number;
  text: string;
  secondaryText?: string;
};

type HeaderPicklistItem = {
  type: 'header';
  key: string;
  value: string;
};

type DividerPicklistItem = {
  type: 'divider';
  key: string;
  className?: string;
};

export type PicklistItem =
  | StandardPicklistItem
  | HeaderPicklistItem
  | DividerPicklistItem;

export type PicklistProps = {
  items: PicklistItem[];
  disabled?: boolean;
  onChange?: (value: StandardPicklistItem | null) => void;
  onSearchChange?: (value: string) => void;
  selectedValues?: StandardPicklistItem[] | null;
  maxItems?: number;
  placeholder?: string;
  className?: HTMLProps<HTMLElement>['className'];
};

const Picklist: FC<PicklistProps> = ({
  placeholder,
  items,
  disabled,
  onChange,
  onSearchChange,
  selectedValues,
  maxItems = 1,
  className,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearchValue] = useState('');

  const setSearch = (value: string): void => {
    setSearchValue(value);
    onSearchChange?.(value);
  };

  const filteredItems = items.filter(item => {
    if (item.type === 'header' || item.type === 'divider') {
      return true;
    }

    return (
      item.value
        ?.toString()
        ?.toLowerCase()
        .includes(search.trim().toLowerCase()) ||
      item.text.toLowerCase().includes(search.trim().toLowerCase()) ||
      item.secondaryText?.toLowerCase().includes(search.trim().toLowerCase())
    );
  });

  const handleItemClick = (value: StandardPicklistItem): void => {
    onChange?.(value);
    setSearch('');
    setIsOpen(false);
  };

  const ref = useRef<HTMLDivElement | null>(null);
  const listContainerRef = useRef<HTMLDivElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);

  useHandleOutsideClicks(ref, () => setIsOpen(false));

  useEffect(() => {
    if (listRef.current) {
      listRef.current.resetAfterIndex(0);
    }
  }, [filteredItems]);

  const listRef = useRef<VariableSizeList | null>(null);

  const shouldShowDropdown = isOpen && items.length > 0;

  const listHeight = Math.min(200, filteredItems.length * 40);

  return (
    <div className={cx('relative', className)} ref={ref}>
      <button
        type="button"
        className={cx(
          'text-left bg-white background w-[200px] h-[30px] flex items-center p-0 border-solid border-[1px] rounded focus:border-primaryDark focus:outline-none overflow-hidden',
          disabled && 'border-gray4',
          !disabled && 'border-gray3 hover:border-gray2 active:border-gray3',
        )}
        onClick={() => {
          setIsOpen(true);
          searchRef.current?.focus();
        }}
      >
        {selectedValues && selectedValues?.length === 1 && (
          <>
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: 200 }}
              transition={{ duration: 0.1, ease: 'easeInOut' }}
              className="flex items-center flex-grow px-2 py-1 text-black whitespace-pre rounded bg-gray4_5 max-w-[200px] box-border"
              title={selectedValues[0].text}
            >
              <div className="w-full overflow-hidden overflow-ellipsis">
                {' '}
                {selectedValues[0].text}
              </div>

              <Button
                variant="icon"
                onClick={() => onChange?.(null)}
                className="flex items-center justify-center w-4 h-4 p-1 ml-2 bg-transparent"
              >
                <Icon iconName="ChromeClose" className="text-[6px]" />
              </Button>
            </motion.div>
          </>
        )}
        {selectedValues &&
          selectedValues?.length > 1 &&
          selectedValues.map((selectedValue, index) => (
            <>
              {selectedValue?.text && (
                <Tooltip text={selectedValue.text}>
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: 'auto' }}
                    transition={{ duration: 0.1, ease: 'easeInOut' }}
                    className="flex items-center px-2 py-1 text-black whitespace-pre bg-gray3 rounded"
                  >
                    <div className="min-w-[60px] max-w-[80px]  overflow-hidden overflow-ellipsis">
                      {' '}
                      {selectedValue.text}
                    </div>

                    <Button
                      variant="icon"
                      onClick={() => onChange?.(null)}
                      className="flex items-center justify-center w-4 h-4 p-1 ml-2 bg-transparent"
                    >
                      <Icon iconName="ChromeClose" className="text-[6px]" />
                    </Button>
                  </motion.div>
                </Tooltip>
              )}
            </>
          ))}
        {(!selectedValues || selectedValues?.length === 0) &&
          !search &&
          !shouldShowDropdown && (
            <>
              <Text
                variant="metadata"
                className={cx(
                  'whitespace-pre pointer-events-none mx-2 my-1',
                  disabled && 'text-gray3',
                  !disabled && 'text-gray2',
                )}
              >
                {placeholder}
              </Text>
              <div className="ml-auto">
                <svg
                  width="12"
                  height="20"
                  viewBox="0 0 12 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="ml-auto"
                >
                  <g clipPath="url(#lz0gr9ro0a)">
                    <path
                      d="M11.853 7.646a.5.5 0 0 1 .001.707l-5.465 5.485a.55.55 0 0 1-.78 0L.146 8.352a.5.5 0 1 1 .708-.706L6 12.812l5.147-5.165a.5.5 0 0 1 .707-.001z"
                      fill="#9E9E9E"
                    />
                  </g>
                  <defs>
                    <clipPath id="lz0gr9ro0a">
                      <path fill="#fff" d="M0 0h12v20H0z" />
                    </clipPath>
                  </defs>
                </svg>
              </div>
            </>
          )}
        {!selectedValues ||
          (selectedValues?.length < maxItems && (
            <input
              disabled={disabled}
              className={cx(
                'flex-grow bg-transparent border-0 outline-none px-2 py-1 w-[150px] box-border',
                document.activeElement !== searchRef.current && 'w-0',
              )}
              value={search}
              ref={searchRef}
              onChange={e => {
                setSearch(e.target.value || '');
                if (listContainerRef.current?.firstElementChild) {
                  listContainerRef.current.firstElementChild.scrollTop = 0;
                }
              }}
            />
          ))}
      </button>

      <motion.div
        className="absolute left-0 z-50 h-40 overflow-y-auto bg-white border-0.5 border-gray-300 rounded shadow-md top-10"
        animate={{
          opacity: shouldShowDropdown ? 1 : 0,
          y: shouldShowDropdown ? 0 : -10,
          width: shouldShowDropdown ? 200 : 0,
          height: shouldShowDropdown ? listHeight : 0,
        }}
        initial={{ opacity: 0, y: -10, width: 0, height: 0 }}
        transition={{ duration: 0.15 }}
        ref={listContainerRef}
      >
        <VariableSizeList
          height={listHeight}
          width={200}
          overscanCount={20}
          itemSize={index => {
            const item = filteredItems[index];
            if (item.type === 'header') {
              return 20;
            }
            if (item.type === 'divider') {
              return 4;
            }
            return item.secondaryText ? 48 : 32;
          }}
          ref={listRef}
          itemCount={filteredItems.length}
        >
          {({ index, style }) => {
            const item = filteredItems[index];
            return (
              <Item
                item={item}
                style={style}
                search={search}
                onClick={handleItemClick}
              />
            );
          }}
        </VariableSizeList>
      </motion.div>
    </div>
  );
};

export default Picklist;
